<template>
  <component-wrapper class="accordion-fi">
    <grid-container-fi>
      <div
        ref="accordionHeader"
        class="accordion-fi__header"
        @click="toggleAccordion"
      >
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="isEditMode"
          class="accordion-fi__header__text"
          v-html="fields.title.editable"
        />
        <div
          v-if="!isEditMode && fields.title?.value"
          class="accordion-fi__header__text"
          v-html="fields.title.value"
        />
        <!-- eslint-enable vue/no-v-html -->

        <icon-fi
          :class="{
            'accordion-fi__header__icon': true,
            'accordion-fi__header__icon--active': isOpen,
          }"
          icon="chevron-down"
          :size="isSM ? '2' : '1'"
        />
      </div>
    </grid-container-fi>

    <slide-up-down-fi
      :model-value="isOpen"
      :duration="700"
    >
      <div class="accordion-fi__content">
        <div
          v-for="(item, idx) in contentItems"
          :key="`item-${idx}`"
          class="accordion-fi__content__item"
        >
          <component
            :is="currentComponent(item.fields.itemType.value)"
            v-if="item?.fields?.itemType?.value"
            :fields="item.fields"
          />
        </div>
      </div>
    </slide-up-down-fi>

    <grid-container-fi>
      <div class="accordion-fi__hr" />
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { IconFi, SlideUpDownFi } from 'atoms';
import { computed, inject, ref, useTemplateRef } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import ImageFi from '@/pages/image-fi/ImageFi.vue';
import SectionDividerFi from '@/pages/section-divider-fi/SectionDividerFi.vue';
import TextMediaFi from '@/pages/text-content/text-media-fi/TextMediaFi.vue';
import TextOneColumnFi from '@/pages/text-content/text-one-column-fi/TextOneColumnFi.vue';
import TextTwoColumnFi from '@/pages/text-content/text-two-column-fi/TextTwoColumnFi.vue';
import type { CheckboxField, SingleLineTextField } from '@/sitecoreFieldTypes';
import { scrollToElement } from '@/utils/smoothScroll';

defineOptions({
  name: 'AccordionFi',
});

const { isSM } = useBreakpoints();

const fields = inject<{
  title?: SingleLineTextField;
  initialOpen: CheckboxField;
  scrollTo: CheckboxField;
  accordionContentItems: any;
}>('fields');

const components: any = {
  ImageFi,
  SectionDividerFi,
  TextMediaFi,
  TextOneColumnFi,
  TextTwoColumnFi,
};

const isOpen = ref<boolean>(Boolean(fields?.initialOpen?.value));
const scrollTo = ref<boolean>(Boolean(fields?.scrollTo?.value));

const accordionHeader = useTemplateRef<HTMLDivElement | undefined>('accordionHeader');

const contentItems = computed(() => fields?.accordionContentItems);

const toggleAccordion = () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value && scrollTo.value) {
    setTimeout(
      () => {
        // @ts-ignore
        scrollToElement(accordionHeader.value);
      },
      isSM.value ? 0 : 300,
    );
  }
};

const currentComponent = (itemType: string) => {
  return components[itemType] || null;
};
</script>

<style scoped src="./accordion-fi" />
